import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Switch
} from '@mui/material';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CancelIcon from '@mui/icons-material/Cancel';
import { Form, Formik } from 'formik';
import { useEffect, useMemo, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import useAuth from '../../context/AuthProvider';
import { UsersModel, UsersModel_VS } from '../../models/UsersModel';
import { apiClient } from '../../helper/api';
import { FMTextField } from '../Elements/input';
import { FMSelectRolesCustomcopy } from './FMSelectRolesCustomcopy';

interface Iprops {
  UsersBL: any; // API user object (camelCase, includes userPartners[])
  handleClose: () => void;
  Open: boolean;
  handleSubmit: () => void;
  initValues: UsersModel;
  setSelectedOption: React.Dispatch<React.SetStateAction<string | string[]>>;
  selectedOptions: string[] | string;
  setSelectedRoles: React.Dispatch<React.SetStateAction<string | string[]>>;
  SelectedRoles: string[] | string;
  ClientUserRoles: string[];
}

const toFormModel = (u: any, fallback: UsersModel): UsersModel => {
  if (!u) return fallback;
  return {
    ...fallback,
    userId: u.userId || 0,
    firstName: u.firstName || '',
    lastName: u.lastName || '',
    userName: u.userName || '',
    userEmail: u.userEmail || '',
    roleIds: u.roleIds || '', // CSV
    isSIPAccess: !!u.isSIPAccess,
    isActive: u.isActive || '',
    partnersIds: (u.userPartners || [])
      .map((p: any) => String(p.partnersId ?? p.PartnersId))
      .filter(Boolean)
      .join(',')
  };
};

type PartnerItem = { partnersId: string; customerName: string };
type RoleItem = { roleId: number; roleName: string; isCustomerRole?: boolean };

export const AddEditCustomerUsers: React.FC<Iprops> = ({
  handleClose,
  Open,
  UsersBL,
  handleSubmit,
  initValues,
  selectedOptions,
  setSelectedOption,
  SelectedRoles,
  setSelectedRoles,
  ClientUserRoles
}) => {
  const { user } = useAuth();
  const token = user?.token;

  const initialValues: UsersModel = useMemo(
    () => toFormModel(UsersBL, initValues),
    [UsersBL, initValues]
  );

  const [SipAccess, SetSipAccess] = useState<boolean>(false);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [selectedMultiPartners, setSelectedMultiPartners] = useState<string[]>([]);
  const [Partner, setpartnerData] = useState<PartnerItem[]>([]);
  const [Roles, setRoles] = useState<RoleItem[]>([{ roleId: 0, roleName: '' }]);
  const [loading, setloading] = useState(false);
  const [diligentSearchPartnerList, setDiligentSearchPartnerList] = useState<PartnerItem[]>([]);

  const selectedPartnerNames = selectedMultiPartners.map((value: string) => {
    const userPartnerEntry = (UsersBL?.userPartners || []).find((x: any) =>
      String(x.partnersId ?? x.PartnersId) === value
    );
    const entry = Partner.find(x => x.partnersId === value);
    const partnerName =
      userPartnerEntry?.customerName ??
      userPartnerEntry?.CustomerName ??
      entry?.customerName ??
      value;
    return partnerName;
  })

  const isDiligentPartner = selectedPartnerNames.length > 0 &&
    selectedPartnerNames.every(name => diligentSearchPartnerList.some(dsp => dsp.customerName === name));

  const userRolesArr = (user?.userRoles || '').split(',');
  const isSuperAdmin = userRolesArr.includes('Super Admin');
  const isMiscFiler = userRolesArr.includes('MISC Filer');
  const partnersDisabled =
    isSuperAdmin ? false : initialValues.roleIds === '2' ? true : isMiscFiler && initialValues.roleIds !== '5' ? false : true;

  // 1) Reset hydration guard whenever dialog opens or user changes
  const didHydrateRef = useRef(false);
  
  useEffect(() => {
    getDiligentSearchPartnerList();
  }, []);

  const getDiligentSearchPartnerList = () => {
    setloading(true);
    apiClient("/DiligentSearch/getDiligentSearchPartnerList", {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }),
    }).then((responseJson) => {
      if (!responseJson) return
      console.log('Diligent Search Partner List:', responseJson.data);
      setDiligentSearchPartnerList(responseJson.data);
    }).finally(() => {
      setloading(false);
    });
  };

  useEffect(() => {
    didHydrateRef.current = false;
  }, [Open, UsersBL?.userId]);

  // 2) Recompute selection whenever dialog opens or payload changes
  useEffect(() => {
    SetSipAccess(initialValues.isSIPAccess ?? false);
    setIsActive(initialValues.isActive ?? true);

    const idsFromUserPartners = (UsersBL?.userPartners || [])
      .map((p: any) => String(p.partnersId ?? p.PartnersId))
      .filter(Boolean);

    const idsFromCsv = String(UsersBL?.partnersIds ?? initialValues?.partnersIds ?? '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);

    const idFromSingle = UsersBL?.partnersId ? [String(UsersBL.partnersId)] : [];

    const union = Array.from(new Set([...idsFromUserPartners, ...idsFromCsv, ...idFromSingle]));
    setSelectedMultiPartners(union);
  }, [Open, UsersBL?.userId, UsersBL?.partnersIds, UsersBL?.userPartners, initialValues.isSIPAccess, initialValues.isActive]);

  // 3) Hydrate from backend (userPartners) on each open (once per open)
  useEffect(() => {
    if (!Open || didHydrateRef.current) return;
    if (!UsersBL?.userId) return;

    apiClient(`/User/getById?id=${UsersBL.userId}`, {
      method: 'GET',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      }),
    })
      .then((res) => {
        if (!res || !res.data) return;
        setIsActive(res.data.isActive);
        const fromUserPartners = (res.data.userPartners || [])
          .map((p: any) => String(p.partnersId ?? p.PartnersId))
          .filter(Boolean);
        if (fromUserPartners.length > 0) {
          setSelectedMultiPartners(prev => Array.from(new Set([...prev, ...fromUserPartners])));
        }
        const partnerItems: PartnerItem[] = (res.data.userPartners || [])
          .map((p: any) => ({
            partnersId: String(p.partnersId ?? p.PartnersId ?? ''),
            customerName: p.customerName ?? p.CustomerName ?? '',
          }))
          .filter((p: PartnerItem) => !!p.partnersId);

        if (partnerItems.length > 0) {
          setpartnerData(prev => {
            const byId: Record<string, PartnerItem> = {};
            [...prev, ...partnerItems].forEach((p: PartnerItem) => { byId[p.partnersId] = p; });
            return Object.values(byId).sort((a, b) => a.customerName.localeCompare(b.customerName, undefined, { sensitivity: 'base' }));
          });
        }
        didHydrateRef.current = true;
      })
      .catch(() => {});
  }, [Open, UsersBL?.userId, token]);

  // Load partners; normalize keys; ensure user’s partners are present in options
  const fetchPartners = () => {
    const endpoint =
      userRolesArr.includes('Client Admin') || userRolesArr.includes('Client Processors')
        ? '/customer/getPartnersList'
        : '/Partners/getPartners';

    apiClient(endpoint, {
      method: 'GET',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      })
    })
      .then(responseJson => {
        if (!responseJson) return;

        const fetched: PartnerItem[] = (responseJson.data || [])
          .map((p: any) => ({
            partnersId: String(p.partnersId ?? p.PartnersId ?? ''),
            customerName: p.customerName ?? p.CustomerName ?? ''
          }))
          .filter((p: PartnerItem) => !!p.partnersId);

        const fromUser: PartnerItem[] = (UsersBL?.userPartners || [])
          .map((p: any) => ({
            partnersId: String(p.partnersId ?? p.PartnersId ?? ''),
            customerName: p.customerName ?? p.CustomerName ?? ''
          }))
          .filter((p: PartnerItem) => !!p.partnersId);

        const byId: Record<string, PartnerItem> = {};
        [...fetched, ...fromUser].forEach((p: PartnerItem) => {
          byId[p.partnersId] = p;
        });

        const sorted = Object.values(byId).sort((a, b) => a.customerName.localeCompare(b.customerName, undefined, { sensitivity: 'base' }));
        setpartnerData(sorted);
      })
      .catch(err => console.error('Error fetching partners:', err));
  };

  // 4) Refresh partners list whenever dialog opens
  useEffect(() => {
    if (Open) fetchPartners();
  }, [Open]);

  // Auto-select all partners for brand-new users
  // For new users, do NOT auto-select all partners. Keep empty until user chooses.

  // Roles
  const fetchRoles = () => {
    apiClient('/Users/getAllRoles', {
      method: 'GET',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      })
    })
      .then(res => {
        if (!res) return;
        
        const filtered: RoleItem[] = res.data.filter((r: RoleItem) => r.isCustomerRole === true)
          .filter((role: RoleItem) => ClientUserRoles.includes(role.roleName));
        setRoles(filtered);
      })
      .catch(err => console.error('Error fetching roles:', err));
  };
  useEffect(() => {
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBoxClose = () => {
    setSelectedMultiPartners([]);
    handleClose();
  };

  const FMSelectMultiPartnersCustom: React.FC<{ name: string }> = () => {
    const handlePartnerChange = (e: any) => {
      const value = e.target.value;
      const selectedValues: string[] = Array.isArray(value) ? value : String(value).split(',');
      setSelectedMultiPartners(selectedValues);
    };

    const handlePartnerRemove = (valueToRemove: string) => {
      setSelectedMultiPartners(prev => prev.filter(id => id !== valueToRemove));
    };

    return (
      <FormControl fullWidth>
        <InputLabel id="partners-select-label">Customers</InputLabel>
        <Select
          label="Client/Partner"
          multiple
          labelId="partners-select-label"
          IconComponent={ExpandMoreIcon}
          disabled={partnersDisabled}
          value={selectedMultiPartners}
          onChange={handlePartnerChange}
          renderValue={() => (
            <Stack gap={1} direction="row" flexWrap="wrap">
              {selectedMultiPartners.map((value: string, index: number) => {
                const partnerName = selectedPartnerNames[index] || value;
                return (
                  <Chip
                    key={value}
                    label={partnerName}
                    onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                    onDelete={partnersDisabled ? undefined : () => handlePartnerRemove(value)}
                    deleteIcon={
                      partnersDisabled ? undefined : (
                        <CancelIcon onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }} />
                      )
                    }
                  />
                );
              })}
            </Stack>
          )}
        >
          {Partner.map(p => (
            <MenuItem key={p.partnersId} value={p.partnersId}>
              {p.customerName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  return (
    <Dialog open={Open} onClose={handleBoxClose} maxWidth="xl">
      {loading && <LinearProgress style={{ margin: 2 }} />}
      {initialValues.userId === 0 ? (
        <h2 style={{ marginLeft: '1vw' }} className="card-title">Create Client User</h2>
      ) : (
        <h2 style={{ marginLeft: '1vw' }} className="card-title">Edit Client User</h2>
      )}

      <Formik
        initialValues={initialValues}
        enableReinitialize
        validateOnChange
        validationSchema={UsersModel_VS}
        onSubmit={async (data, { setSubmitting, setErrors, setFieldValue }) => {
          try {
            let selectedRoleIds = data.roleIds?.split(',').filter(Boolean) || [];
            // If isDiligentUser is false, remove "Diligent Search User" role if it's selected
            if (!isDiligentPartner && selectedRoleIds.length > 0) {
              const diligentRole = Roles.find(r => r.roleName === 'Diligent Search User');
              if (diligentRole && selectedRoleIds.includes(String(diligentRole.roleId))) {
                selectedRoleIds = selectedRoleIds.filter(id => id !== String(diligentRole.roleId));
                // Update formik field value to reflect the change
                setFieldValue('roleIds', selectedRoleIds.join(','));
              }
            }
            if (!selectedRoleIds || selectedRoleIds.length < 1) {
              setErrors({ roleIds: 'Please select Role' } as any);
              setSubmitting(false);
              return;
            }

            const isClientUser =
              userRolesArr.includes('Client Admin') || userRolesArr.includes('Client Processors');

            if (selectedMultiPartners.length === 0 && !isClientUser) {
              alert('Customer required');
              setSubmitting(false);
              return;
            }

            let finalPartnerIds = selectedMultiPartners;
            if (isClientUser && selectedMultiPartners.length === 0) {
              finalPartnerIds = Partner.map(p => p.partnersId);
            }

            const primaryPartnerId = finalPartnerIds.length ? Number(finalPartnerIds[0]) : 0;

            const requestData = {
              ...data,
              roleIds: selectedRoleIds.join(', '),
              isSIPAccess: SipAccess,
              isActive: isActive,
              partnersId: primaryPartnerId, // TODO: Investigate the duplication between user.PartnersId and the UserPartners table.
              MultiPartnersIds: finalPartnerIds,
              partnersIds: finalPartnerIds.join(', '), // Saved to UserPartners and helps identify diligent users
                                                       // by checking that all partners are in the VenGroup.
              customerName: finalPartnerIds
                .map(id => Partner.find(p => p.partnersId === id)?.customerName)
                .filter(Boolean)
                .join(', ') // optional helper
            };

            setloading(true);

            const response = await apiClient('/Users/AddUpdate', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
              },
              body: JSON.stringify(requestData)
            });

            const isCreate = (initialValues.userId ?? 0) === 0;
            if (response && response.resp !== 1) {
              handleSubmit();
              await Swal.fire({
                title: isCreate ? 'User added successfully' : 'User updated successfully',
                text: '',
                icon: 'success',
                confirmButtonText: 'OK'
              });
              // Clear role selection only after a successful save
              setSelectedOption([]);
              setSelectedMultiPartners([]);
              handleBoxClose();
            } else {
              const backendMessage = String(response?.message ?? '').toLowerCase();
              let warnMsg = 'Username or email already exists';
              if (backendMessage.includes('username')) {
                warnMsg = 'Username already exists';
              } else if (backendMessage.includes('email')) {
                warnMsg = 'Email already exists';
              }
              await Swal.fire({
                title: warnMsg,
                text: '',
                icon: 'warning',
                confirmButtonText: 'OK'
              });
            }
          } catch (error) {
            console.error('Error submitting form:', error);
            alert('An error occurred while saving the data');
          } finally {
            setSubmitting(false);
            setloading(false);
          }
        }}
      >
        {({ isSubmitting }) => {
          const showSipAccess = isSuperAdmin;
          const showCustomers = isSuperAdmin;
          return (
            <Form className="main-form">
              <DialogContent>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <FormControl sx={{ width: '100%' }}>
                      <FormHelperText className="formtexthelper-heading">First Name</FormHelperText>
                      <FMTextField name="firstName" autoFocus margin="dense" label={null} type="text" fullWidth variant="outlined" sx={{ m: 2 }} />
                    </FormControl>
                  </Grid>

                  <Grid item xs={6}>
                    <FormControl sx={{ width: '100%' }}>
                      <FormHelperText className="formtexthelper-heading">Last Name</FormHelperText>
                      <FMTextField name="lastName" margin="dense" label={null} type="text" fullWidth variant="outlined" sx={{ m: 2 }} />
                    </FormControl>
                  </Grid>

                  <Grid item xs={6}>
                    <FormControl sx={{ width: '100%' }}>
                      <FormHelperText className="formtexthelper-heading">Username</FormHelperText>
                      <FMTextField name="userName" autoComplete="off" margin="dense" label={null} type="text" fullWidth variant="outlined" sx={{ m: 2 }} />
                    </FormControl>
                  </Grid>

                  <Grid item xs={6}>
                    <FormControl sx={{ width: '100%' }}>
                      <FormHelperText className="formtexthelper-heading">Email</FormHelperText>
                      <FMTextField name="userEmail" margin="dense" label={null} type="email" fullWidth variant="outlined" sx={{ m: 2 }} />
                    </FormControl>
                  </Grid>

                  {showCustomers && (
                    <Grid item xs={6}>
                        <FormControl sx={{ width: '100%' }}>
                        <FMSelectMultiPartnersCustom name="partnersIds" />
                        </FormControl>
                    </Grid>
                    )}
                  <Grid item xs={6}>
                    <FormHelperText className="formtexthelper-heading">Roles</FormHelperText>
                    <FormControl sx={{ width: '100%' }}>
                      <FMSelectRolesCustomcopy name="roleIds" UsersBL={UsersBL} Roles={Roles} isDiligent={isDiligentPartner} />

                    </FormControl>
                  </Grid>

                  {showSipAccess && (
                    <Grid item >
                      <FormControlLabel
                        control={
                          <Switch
                            checked={SipAccess}
                            onChange={e => SetSipAccess(e.target.checked)}
                            color="primary"
                          />
                        }
                        label="SIP Access"
                      />
                    </Grid>
                  )}

                  {(userRolesArr.includes('Super Admin') || userRolesArr.includes('Customer Admin')||userRolesArr.includes('Client Admin')) && (
                    <Grid item >
                      <FormControlLabel
                        control={
                          <Switch
                            checked={isActive}
                            onChange={e => setIsActive(e.target.checked)}
                            color="primary"
                          />
                        }
                        label={isActive ? "Active" : "Inactive"}
                      />
                    </Grid>
                  )}
                </Grid>
              </DialogContent>

              <DialogActions>
                <button className="btn-2 btn d-flex btn-primary search-btn" onClick={handleBoxClose} type="button">
                  Cancel
                </button>
                <button className="btn btn-primary active-btn-blue search-btn" type="submit" disabled={isSubmitting}>
                  Submit
                </button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};